/* eslint-disable @typescript-eslint/no-explicit-any */

import type {
  GlobalSearchShellExtension,
  GlobalSearchAction,
  GlobalSearchCategory,
  GlobalSearchCategoryColumn,
  GlobalSearchEmptyState,
  GlobalSearchAvatar,
  GlobalSearchQuery,
} from '../global-search-extension'
import type { Settings } from '../settings-definition'
import type { ShellExtension, ShellExtensionCustomElements, ShellExtensionQuickActions } from '../shell-extension'

class SchemaError extends Error {
  constructor(public key: string, public actualType: string, public expectedType: string, public message: string = '') {
    super()
  }
}

const assertSchema = <T extends object>(object: any, schema: T): void => {
  const keys = Object.keys(schema) as unknown as readonly (keyof T)[]
  keys.some(key => {
    if (typeof object?.[key] !== typeof schema[key]) {
      throw new SchemaError(key as string, typeof object?.[key], typeof schema[key], '')
    }
  })
}

/**
 * Validates if the object passed is a shell extension
 * @param extension extension to validate
 */
export const assertShellExtension = (extension: any): void => {
  /* istanbul ignore next -> this is only a schema object so the functions are never called */
  const schema: ShellExtension = {
    id: '',
    initialize: async () => {},
    registerNamespaces: async () => {},
    getString: () => '',
    getComponentById: async () => null,
    getModules: () => [],
    getSettings: () => [],
    getNavigationItems: () => [],
    getMetadata: () => ({ id: '', name: '', version: '' }),
  }

  try {
    assertSchema(extension, schema)
  } catch (e: any) {
    const message = `The extension is not compliant with the ShellExtension interface. The ${extension.id}.${e.key} type should be ${e.expectedType} instead of ${e.actualType}.`
    throw new SchemaError(e.key, e.actualType, e.expectedType, message)
  }
}

const isPropertyOfType = (property: any, types: readonly string[]): boolean => types.includes(typeof property)

const isSchemaSupported = <T extends object>(object: any, schema: T): object is T => {
  const keys = Object.keys(schema) as unknown as readonly (keyof T)[]
  return !keys.some(key => typeof object?.[key] !== typeof schema[key])
}

/**
 * Validates if an extension support custom elements interface
 * @param extension extension to validate
 * @returns true if extension support custom elements interface, false otherwise
 */
export const supportsCustomElements = (extension: ShellExtension): extension is ShellExtensionCustomElements => {
  /* istanbul ignore next -> this is only a schema object so the functions are never called */
  const schema: ShellExtensionCustomElements = {
    id: '',
    initialize: async () => {},
    registerNamespaces: async () => {},
    getString: () => '',
    getComponentById: async () => null,
    getModules: () => [],
    getSettings: () => [],
    getNavigationItems: () => [],
    getMetadata: () => ({ id: '', name: '', version: '' }),
    registerCustomElements: () => {},
  }
  return isSchemaSupported(extension, schema)
}

/**
 * Validates if an extension support quick actions interface
 * @param extension extension to validate
 */
export const assertQuickActions = (extension: ShellExtension): void => {
  /* istanbul ignore next -> this is only a schema object so the functions are never called */
  const schema: ShellExtensionQuickActions = {
    id: '',
    initialize: async () => {},
    registerNamespaces: async () => {},
    getString: () => '',
    getComponentById: async () => null,
    getModules: () => [],
    getSettings: () => [],
    getNavigationItems: () => [],
    getMetadata: () => ({ id: '', name: '', version: '' }),
    getQuickActions: () => [],
  }
  try {
    assertSchema(extension, schema)
  } catch (e: any) {
    const message = `The extension is not compliant with the ShellExtensionQuickActions interface. The ${extension.id}.${e.key} type should be ${e.expectedType} instead of ${e.actualType}.`
    throw new SchemaError(e.key, e.actualType, e.expectedType, message)
  }
}

/**
 * Check Settings type
 * @param settings
 * @returns
 */
export const isSettings = (settings: any): settings is Settings => {
  /* istanbul ignore next -> this is only a schema object so the functions are never called */
  const schemaSettings: Settings = {
    displayNameKey: '',
    componentId: '',
    route: '',
  }

  const keys = Object.keys(schemaSettings) as readonly (keyof Settings)[]
  return !keys.some(key => typeof settings?.[key] !== typeof schemaSettings[key])
}

/**
 * Validates if an extension support global search interface
 * @param extension extension to validate
 */
export const assertGlobalSearch = (extension: ShellExtension): void => {
  /* istanbul ignore next -> this is only a schema object so the functions are never called */
  const schema: GlobalSearchShellExtension = {
    id: '',
    initialize: async () => {},
    registerNamespaces: async () => {},
    getString: () => '',
    getComponentById: async () => null,
    getModules: () => [],
    getSettings: () => [],
    getNavigationItems: () => [],
    getMetadata: () => ({ id: '', name: '', version: '' }),
    getGlobalSearchCategories: () => [],
    getGlobalSearchResultApplication: () => '',
    getGlobalSearchResultActions: () => [],
    getGlobalSearchResultDefaultAction: () => undefined,
    getActionsForSearchString: () => [],
  }
  try {
    assertSchema(extension, schema)
  } catch (e: any) {
    const message = `The extension is not compliant with the GlobalSearchShellExtension interface. The ${extension.id}.${e.key} type should be ${e.expectedType} instead of ${e.actualType}.`
    throw new SchemaError(e.key, e.actualType, e.expectedType, message)
  }
}

/**
 * Validate if an object is a global search action
 * @param object object to validate
 */
export const assertGlobalSearchAction = (object: any): void => {
  /* istanbul ignore next -> this is only a schema object so the functions are never called */
  const schema: GlobalSearchAction = {
    id: '',
    command: {
      commandName: '',
      namespace: '',
    },
    displayNameKey: '',
    icon: '',
  }
  try {
    assertSchema(object, schema)
  } catch (e: any) {
    const message = `The object is not compliant with the GlobalSearchAction interface. The ${object.id}.${e.key} type should be ${e.expectedType} instead of ${e.actualType}.`
    throw new SchemaError(e.key, e.actualType, e.expectedType, message)
  }
  try {
    assertSchema(object.command, schema.command)
  } catch (e: any) {
    const message = `The object is not compliant with the GlobalSearchAction interface. The ${object.id}.command.${e.key} type should be ${e.expectedType} instead of ${e.actualType}.`
    throw new SchemaError(e.key, e.actualType, e.expectedType, message)
  }
}

/**
 * Validate if an object is a global search category column
 * @param object object to validate
 */
export const assertGlobalSearchCategoryColumn = (object: any): void => {
  /* istanbul ignore next -> this is only a schema object so the functions are never called */
  const schema: Omit<GlobalSearchCategoryColumn, 'valueField'> = {
    displayNameKey: '',
    id: '',
  }
  try {
    assertSchema(object, schema)
  } catch (e: any) {
    const message = `The object is not compliant with the GlobalSearchCategoryColumn interface. The ${object.id}.${e.key} type should be ${e.expectedType} instead of ${e.actualType}.`
    throw new SchemaError(e.key, e.actualType, e.expectedType, message)
  }
  assertGlobalSearchValueField((object as GlobalSearchCategoryColumn).valueField, 'valueField')
}

/**
 * Validate if an object is a global search category avatar
 * @param object object to validate
 */
export const assertGlobalSearchAvatar = (object: any): void => {
  assertGlobalSearchValueField((object as GlobalSearchAvatar)?.userKey, 'userKey')
  assertGlobalSearchValueField((object as GlobalSearchAvatar)?.familyName, 'familyName')
  assertGlobalSearchValueField((object as GlobalSearchAvatar)?.givenName, 'givenName')
}

/**
 * Validate if a property is a global search value field
 * @param property property to validate
 */
export const assertGlobalSearchValueField = (property: any, key: string): void => {
  if (isPropertyOfType(property, ['string', 'function'])) {
    return
  }
  const e = {
    key,
    message: `The ${key} is not compliant with the GlobalSearchValueField interface. The property type should be string or function instead of ${typeof property}.`,
    expectedType: 'String or Function',
    actualType: typeof property,
  }
  throw new SchemaError(e.key, e.actualType, e.expectedType, e.message)
}

/**
 * Validate if a given object is a valid empty state object
 * @param object object to validate
 */
export const assertGlobalSearchEmptyState = (object: any): void => {
  /* istanbul ignore next -> this is only a schema object so the functions are never called */
  const schema: GlobalSearchEmptyState = {
    messageKey: '',
    actions: [],
  }
  try {
    assertSchema(object, schema)
  } catch (e: any) {
    const message = `The object is not compliant with the GlobalSearchEmptyState interface. The ${e.key} type should be ${e.expectedType} instead of ${e.actualType}.`
    throw new SchemaError(e.key, e.actualType, e.expectedType, message)
  }
  ;(object as GlobalSearchEmptyState).actions.forEach(action => assertGlobalSearchAction(action))
}

/**
 * Validates if the avatar info or icon is present and if so calls the function to assert their type
 * @param avatarFields object to validate
 * @param iconField property to validate
 */
export const assertIconOrAvatar = (avatarFields: any, iconField: any, categoryId: any): void => {
  if (avatarFields) {
    assertGlobalSearchAvatar(avatarFields)
  } else if (iconField) {
    assertGlobalSearchValueField(iconField, 'iconField')
  } else {
    const e = {
      key: 'avatarFields or iconField',
      message: `You must provide either the avatar information or an icon with your ${categoryId} GlobalSearchCategory object`,
      expectedType: 'GlobalSearchAvatar or GlobalSearchValueField',
      actualType: 'none',
    }
    throw new SchemaError(e.key, e.actualType, e.expectedType, e.message)
  }
}

/**
 * Validate if an object is a global search category
 * @param object object to validate
 */
export const assertGlobalSearchCategory = (object: any): void => {
  /* istanbul ignore next -> this is only a schema object so the functions are never called */
  const schema: Omit<
    GlobalSearchCategory,
    'titleField' | 'previewField' | 'avatarFields' | 'iconField' | 'emptyState'
  > = {
    id: '',
    application: '',
    columns: [],
    getQueryForSearchString: () =>
      ({ application: '', query: '', permissionParameters: { owner_id: [], subowner_id: [] } } as GlobalSearchQuery),
  }
  const category: GlobalSearchCategory = object as GlobalSearchCategory
  try {
    assertSchema(category, schema)
  } catch (e: any) {
    const message = `The object is not compliant with the GlobalSearchCategory interface. The ${object.id}.${e.key} type should be ${e.expectedType} instead of ${e.actualType}.`
    throw new SchemaError(e.key, e.actualType, e.expectedType, message)
  }
  assertGlobalSearchValueField(category.previewField, 'previewField')
  assertGlobalSearchValueField(category.titleField, 'titleField')
  assertIconOrAvatar(category.avatarFields, category.iconField, category.id)
  category.columns?.forEach(column => assertGlobalSearchCategoryColumn(column))
  assertGlobalSearchEmptyState(category.emptyState)
}
